var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, useState } from "react";
import styled from "styled-components";
import { Flex } from "./Flex";
import { Button } from "./Button";
import { Icon } from "./Icon";
/**
 * A container that displays a subset of items, inlined (separated by commas followed by an ellipsis) or as a list (one item per line).
 * It expands to to the full set of items depending on how many items are on the list and the prop maxWhenCollapsed.
 * If the total number of items is larger than maxWhenCollapsed, the icon [+] is shown and one can toggle
 * between an opened (full set of items) and a closed list (showing a subset of items) by clicking on the [+] or [-] icon.
 *
 * It can take any BoxProps.
 * @example
 *  <Foldable inline={isInline} justifyContent="flex-start" maxWhenCollapsed={columnDef.max}>
 *    {ipAddress.map(({ ipAddress: ip, entityId }) => (
 *      <Text key={entityId} textStyle="default">
 *        {ip}
 *      </Text>
 *    ))}
 *  </Foldable>
 */
export const Foldable = (_a) => {
    var { children, inline = false, maxWhenCollapsed = 1, separator = ", ", showLessLabel = "see less", showMoreLabel = "see more" } = _a, boxProps = __rest(_a, ["children", "inline", "maxWhenCollapsed", "separator", "showLessLabel", "showMoreLabel"]);
    const [visible, setVisible] = useState(false);
    const maxWhenCollapsedValue = maxWhenCollapsed < 1 ? 1 : maxWhenCollapsed;
    const icon = visible ? "CellClose" : "CellOpen";
    const showEllipsis = inline && !visible && Children.count(children) > maxWhenCollapsedValue;
    return (React.createElement(Flex, Object.assign({ display: inline ? "block" : "flex", "data-testid": "Foldable", alignItems: "flex-end" }, boxProps),
        React.createElement(Container, { inline: inline }, Children.toArray(children).map((child, i, arr) => {
            // Don't render beyond max when not expanded
            if (i >= maxWhenCollapsedValue && !visible) {
                return undefined;
            }
            const childCount = arr.length;
            const isLast = visible ? i + 1 === childCount : i + 1 === maxWhenCollapsedValue;
            const showToggle = childCount > maxWhenCollapsedValue;
            return (React.createElement(ChildWrapper, { key: i },
                child,
                !isLast && inline && React.createElement(Separator, { value: separator }),
                isLast && showToggle && (React.createElement(React.Fragment, null,
                    showEllipsis && React.createElement(React.Fragment, null, "\u00A0..."),
                    React.createElement(Toggle, { "aria-expanded": visible, "aria-label": visible ? showLessLabel : showMoreLabel, "data-testid": "foldableToggleButton", onClick: (e) => {
                            e.stopPropagation();
                            setVisible(!visible);
                        }, size: "noPadding", textStyle: "greyLink", type: "button", variant: "iconWithoutBorder" },
                        React.createElement(Icon, { size: "17" // Larger than 17 will increase the line height because our icons are made square
                            , src: icon }))))));
        }))));
};
const Separator = ({ value }) => React.createElement("span", { style: { whiteSpace: "pre" } }, value);
const Container = styled.div `
  display: ${({ inline }) => (inline ? "inline-flex" : "flex")};
  flex-wrap: wrap;
  /* stylelint-disable-next-line declaration-block-no-redundant-longhand-properties -- we want this to be explicit */
  flex-direction: ${({ inline }) => (inline ? "row" : "column")};
`;
const ChildWrapper = styled.div `
  display: inline;
`;
const Toggle = styled(Button) `
  margin-left: ${({ theme }) => `${theme.space[1]}px`};
  margin-bottom: 2px;
  vertical-align: bottom;

  /* inline SVG was causing layout problems */
  svg {
    display: block;
  }
`;
